import React, { useState } from 'react';
import {
  X, CheckCircle2, Ban, Unlock, RefreshCw, ChevronRight,
  Clock, ArrowRight, Package, RotateCcw,
} from 'lucide-react';
import { cn } from '../../../ui/utils';
import { ConfirmDeleteDialog, type DeleteConfirmState } from '../../../ui/ConfirmDeleteDialog';
import type { ReviewAction, CampaignSubmission } from '../types';

/* ────────────────────────────────────────────────────────────────────────────
 * HistoryAuditPopup — Deep Audit View
 *
 * Full lifecycle history of a submission showing every action
 * with timestamps, actor names, change details, and diff badges.
 * Reusable shared component from src/modules/aggregator/components/.
 * ──────────────────────────────────────────────────────────────────────────── */

interface HistoryAuditPopupProps {
  submission: CampaignSubmission;
  onClose: () => void;
  onRevert?: (submission: CampaignSubmission) => void;
}

const ACTION_META: Record<string, { label: string; icon: React.ReactNode; bg: string; text: string; border: string }> = {
  APPROVE:  { label: 'Approved',       icon: <CheckCircle2 size={16} />, bg: 'bg-emerald-50',  text: 'text-emerald-700', border: 'border-emerald-200' },
  REJECT:   { label: 'Rejected',       icon: <Ban size={16} />,          bg: 'bg-red-50',      text: 'text-red-700',     border: 'border-red-200'     },
  UNLOCK:   { label: 'Unlocked Item',  icon: <Unlock size={16} />,       bg: 'bg-amber-50',    text: 'text-amber-700',   border: 'border-amber-200'   },
  RESUBMIT: { label: 'Resubmitted',    icon: <RefreshCw size={16} />,    bg: 'bg-indigo-50',   text: 'text-indigo-700',  border: 'border-indigo-200'  },
};

/** Parse a change detail string like "Updated Price from 20.00 to 22.50" into parts */
function parseChangeBadge(detail: string): { label: string; from?: string; to?: string } | null {
  // Price
  const priceMatch = detail.match(/Updated Price from ([\d.]+) to ([\d.]+)/);
  if (priceMatch) return { label: 'Price Change', from: priceMatch[1], to: priceMatch[2] };
  // Rename
  const renameMatch = detail.match(/Renamed from "(.+?)" to "(.+?)"/);
  if (renameMatch) return { label: 'Name Change', from: renameMatch[1], to: renameMatch[2] };
  // Photo
  if (detail.includes('photo') || detail.includes('Photo')) return { label: 'Photo Change' };
  // Description
  if (detail.includes('description') || detail.includes('Description')) return { label: 'Description Change' };
  return null;
}

export function HistoryAuditPopup({ submission, onClose, onRevert }: HistoryAuditPopupProps) {
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState | null>(null);
  const history = [...submission.reviewHistory].sort((a, b) => b.timestamp - a.timestamp);

  return (
    <>
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between shrink-0">
          <div>
            <h3 className="text-lg font-bold text-zinc-900">Full Audit Trail</h3>
            <p className="text-xs text-zinc-400 mt-0.5">{submission.campaignName} — {submission.brandName} — {submission.items.length} item(s)</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-zinc-100 rounded-lg transition-all">
            <X size={18} className="text-zinc-400" />
          </button>
        </div>

        {/* Timeline */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {history.length === 0 ? (
            <div className="text-center py-12">
              <Clock size={32} className="mx-auto text-zinc-200 mb-3" />
              <p className="text-sm text-zinc-400">No review actions recorded yet.</p>
            </div>
          ) : (
            <div className="relative">
              {/* Vertical timeline line */}
              <div className="absolute left-[19px] top-3 bottom-3 w-[2px] bg-zinc-200" />

              <div className="space-y-0">
                {history.map((action, i) => {
                  const meta = ACTION_META[action.action] ?? ACTION_META.APPROVE;
                  return (
                    <div key={i} className="relative flex gap-4 pb-5">
                      {/* Timeline dot */}
                      <div className={cn("relative z-10 w-10 h-10 rounded-full flex items-center justify-center shrink-0", meta.bg, meta.border, "border-2")}>
                        <span className={meta.text}>{meta.icon}</span>
                      </div>

                      {/* Content */}
                      <div className={cn("flex-1 min-w-0 rounded-xl px-4 py-3 border", meta.bg, meta.border)}>
                        <div className="flex items-center justify-between gap-2">
                          <p className={cn("text-sm font-bold", meta.text)}>
                            [{action.departmentName}] {meta.label}
                          </p>
                          <div className="flex items-center gap-2 shrink-0">
                            {i === 0 && onRevert && action.action !== 'UNLOCK' && (
                              <button
                                onClick={() => setDeleteConfirm({ message: `Revert this ${action.action} decision by ${action.reviewerName}?`, onConfirm: () => onRevert(submission) })}
                                className="p-1 rounded-md hover:bg-red-100 text-zinc-400 hover:text-red-600 transition-all"
                                title="Revert this decision"
                              >
                                <RotateCcw size={12} />
                              </button>
                            )}
                            <span className="text-[10px] text-zinc-400">{new Date(action.timestamp).toLocaleString()}</span>
                          </div>
                        </div>
                        <p className="text-xs text-zinc-500 mt-0.5">by {action.reviewerName}</p>

                        {action.note && (
                          <p className="text-xs text-zinc-600 mt-2 italic">"{action.note}"</p>
                        )}

                        {/* Unlocked items */}
                        {action.unlockedItemIds && action.unlockedItemIds.length > 0 && (
                          <div className="mt-2 flex items-center gap-1.5 text-xs text-amber-600">
                            <Unlock size={11} /> {action.unlockedItemIds.length} item(s) unlocked for correction
                          </div>
                        )}

                        {/* Change details with diff badges */}
                        {action.changeDetails && action.changeDetails.length > 0 && (
                          <div className="mt-2 space-y-1.5">
                            {action.changeDetails.map((cd, ci) => {
                              const badge = parseChangeBadge(cd);
                              return (
                                <div key={ci} className="flex items-center gap-2 text-xs">
                                  <ChevronRight size={10} className="text-indigo-400 shrink-0" />
                                  <span className="text-indigo-700">{cd}</span>
                                  {badge && badge.from && badge.to && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-[9px] font-bold border border-indigo-200">
                                      {badge.label}: <span className="line-through opacity-60">{badge.from}</span> <ArrowRight size={8} /> <span className="font-black">{badge.to}</span>
                                    </span>
                                  )}
                                  {badge && !badge.from && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-[9px] font-bold border border-indigo-200">
                                      {badge.label}
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer summary */}
        <div className="border-t border-zinc-200 px-6 py-3 flex items-center justify-between shrink-0 bg-zinc-50">
          <div className="flex items-center gap-4 text-xs text-zinc-500">
            <span className="flex items-center gap-1"><CheckCircle2 size={11} className="text-emerald-500" /> {history.filter(a => a.action === 'APPROVE').length} Approvals</span>
            <span className="flex items-center gap-1"><Unlock size={11} className="text-amber-500" /> {history.filter(a => a.action === 'UNLOCK').length} Unlocks</span>
            <span className="flex items-center gap-1"><RefreshCw size={11} className="text-indigo-500" /> {history.filter(a => a.action === 'RESUBMIT').length} Resubmits</span>
            {history.filter(a => a.action === 'REJECT').length > 0 && (
              <span className="flex items-center gap-1"><Ban size={11} className="text-red-500" /> {history.filter(a => a.action === 'REJECT').length} Rejections</span>
            )}
          </div>
          <button onClick={onClose} className="px-4 py-1.5 border border-zinc-200 rounded-lg text-xs font-semibold text-zinc-600 hover:bg-zinc-100 transition-all">
            Close
          </button>
        </div>
      </div>
    </div>
    <ConfirmDeleteDialog state={deleteConfirm} onClose={() => setDeleteConfirm(null)} />
    </>
  );
}
